import React, { useState, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import PropsType from 'prop-types';

// Kept in sync with the canonical Button variants (Phase 3).
const VARIANT_CLASSES = {
  primary:   'tw-text-white tw-bg-primary-600 tw-border tw-border-primary-600 hover:tw-bg-primary-700 hover:tw-border-primary-700',
  danger:    'tw-text-white tw-bg-red-600 tw-border tw-border-red-600 hover:tw-bg-red-700 hover:tw-border-red-700',
  success:   'tw-text-white tw-bg-green-600 tw-border tw-border-green-600 hover:tw-bg-green-700 hover:tw-border-green-700',
  secondary: 'tw-text-slate-700 tw-bg-white tw-border tw-border-slate-300 hover:tw-bg-slate-50',
};

const resolveVariant = (button, index) => {
  if (button.className) return button.className;
  if (button.variant) return VARIANT_CLASSES[button.variant] || VARIANT_CLASSES.primary;
  const lbl = button.label.toLowerCase();
  if (lbl === 'yes' || lbl === 'delete') return VARIANT_CLASSES.danger;
  return index === 0 ? VARIANT_CLASSES.primary : VARIANT_CLASSES.secondary;
};

const ICON_CONFIG = {
  warning: {
    outer: 'tw-bg-amber-100',
    inner: 'tw-bg-amber-400',
    svg: (
      <svg viewBox="0 0 24 24" fill="currentColor">
        <path fillRule="evenodd" clipRule="evenodd"
          d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8a1 1 0 0 1 1 1v3a1 1 0 1 1-2 0V9a1 1 0 0 1 1-1zm0 8a1 1 0 1 0 0-2 1 1 0 0 0 0 2z" />
      </svg>
    ),
  },
  danger: {
    outer: 'tw-bg-red-100',
    inner: 'tw-bg-red-500',
    svg: (
      <svg viewBox="0 0 24 24" fill="currentColor">
        <path fillRule="evenodd" clipRule="evenodd"
          d="M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2zm-1 6a1 1 0 1 1 2 0v4a1 1 0 1 1-2 0V8zm1 8a1 1 0 1 0 0-2 1 1 0 0 0 0 2z" />
      </svg>
    ),
  },
  info: {
    outer: 'tw-bg-blue-100',
    inner: 'tw-bg-blue-500',
    svg: (
      <svg viewBox="0 0 24 24" fill="currentColor">
        <path fillRule="evenodd" clipRule="evenodd"
          d="M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2zm1 7a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm-1 3a1 1 0 0 1 1 1v3a1 1 0 1 1-2 0v-3a1 1 0 0 1 1-1z" />
      </svg>
    ),
  },
  success: {
    outer: 'tw-bg-emerald-100',
    inner: 'tw-bg-emerald-500',
    svg: (
      <svg viewBox="0 0 24 24" fill="currentColor">
        <path fillRule="evenodd" clipRule="evenodd"
          d="M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2zm4.707 7.293a1 1 0 0 0-1.414 0L11 13.586l-1.293-1.293a1 1 0 0 0-1.414 1.414l2 2a1 1 0 0 0 1.414 0l5-5a1 1 0 0 0 0-1.414z" />
      </svg>
    ),
  },
};

const DialogIcon = ({ type = 'warning' }) => {
  const cfg = ICON_CONFIG[type] || ICON_CONFIG.warning;
  return (
    <div className={`tw-w-14 tw-h-14 tw-rounded-xl tw-flex tw-items-center tw-justify-center tw-shrink-0 ${cfg.outer}`}>
      <div className={`tw-w-9 tw-h-9 tw-rounded-xl tw-flex tw-items-center tw-justify-center ${cfg.inner}`}>
        <span className="tw-w-5 tw-h-5 tw-text-white">{cfg.svg}</span>
      </div>
    </div>
  );
};
DialogIcon.propTypes = {
  type: PropsType.oneOf(['warning', 'danger', 'info', 'success']),
};

const ConfirmationDialog = ({ config, onClose }) => {
  const [processingButton, setProcessingButton] = useState(null);

  const handleButtonClick = async (button, index) => {
    if (processingButton !== null) return;
    setProcessingButton(index);
    try {
      if (button.onClick) await button.onClick();
      onClose();
    } finally {
      setProcessingButton(null);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && config.closeOnClickOutside) onClose();
  };

  useEffect(() => {
    if (!config.closeOnEscape) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [config.closeOnEscape, onClose]);

  if (!config) return null;

  return (
    <div
      className="tw-fixed tw-inset-0 tw-flex tw-items-center tw-justify-center tw-p-4 tw-bg-black/15"
      style={{ zIndex: config.zIndex || 999999 }}
      onClick={handleBackdropClick}
    >
      <div className="tw-relative tw-w-full tw-max-w-sm tw-pointer-events-auto">
        <div className="tw-bg-white tw-rounded-2xl tw-shadow-2xl tw-outline-none">
          <div className="tw-p-5">
            {/* Header */}
            <div className="tw-flex tw-items-center tw-gap-3 tw-mb-4">
              <DialogIcon type={config.type} />
              {config.title && (
                <h4 className="tw-text-lg tw-font-semibold tw-text-gray-900 tw-m-0 tw-leading-snug tw-flex-1">
                  {config.title}
                </h4>
              )}
            </div>

            {/* Message */}
            <div
              className="tw-text-gray-500 tw-text-sm tw-leading-snug tw-mb-5 tw-ml-[68px]"
              dangerouslySetInnerHTML={{ __html: config.message }}
            />

            {/* Buttons */}
            <div className="tw-flex tw-justify-end tw-gap-2">
              {config.buttons.map((button, index) => (
                <button
                  key={index}
                  type="button"
                  className={`tw-inline-flex tw-items-center tw-justify-center tw-min-w-[70px] tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-rounded-md tw-transition-colors tw-cursor-pointer disabled:tw-opacity-50 disabled:tw-cursor-not-allowed focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-400/50 ${resolveVariant(button, index)}`}
                  onClick={() => handleButtonClick(button, index)}
                  disabled={processingButton !== null}
                >
                  {processingButton === index && (
                    <span className="tw-inline-block tw-w-4 tw-h-4 tw-border-2 tw-border-current tw-border-r-transparent tw-rounded-xl tw-animate-spin tw-mr-2" />
                  )}
                  {processingButton === index ? 'Processing…' : button.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

ConfirmationDialog.propTypes = {
  config: PropsType.shape({
    title:               PropsType.string,
    message:             PropsType.string,
    type:                PropsType.oneOf(['warning', 'danger', 'info', 'success']),
    closeOnEscape:       PropsType.bool,
    closeOnClickOutside: PropsType.bool,
    overlayClassName:    PropsType.string,
    zIndex:              PropsType.number,
    buttons: PropsType.arrayOf(PropsType.shape({
      label:   PropsType.string.isRequired,
      onClick: PropsType.func.isRequired,
    })).isRequired,
  }).isRequired,
  onClose: PropsType.func.isRequired,
};

// ── imperative API ──────────────────────────────────────────────────────────

let confirmationContainer = null;
let confirmationRoot = null;

const getConfirmationContainer = () => {
  if (!confirmationContainer) {
    confirmationContainer = document.createElement('div');
    confirmationContainer.id = 'confirmation-alert-container';
    confirmationContainer.style.cssText = 'position:fixed;top:0;left:0;pointer-events:none;z-index:999999';
    document.body.appendChild(confirmationContainer);
  }
  return confirmationContainer;
};

const cleanupConfirmation = () => {
  if (confirmationRoot) {
    confirmationRoot.unmount();
    confirmationRoot = null;
  }
  if (confirmationContainer?.parentNode) {
    confirmationContainer.parentNode.removeChild(confirmationContainer);
    confirmationContainer = null;
  }
};

const confirmAlert = (config) => {
  const yesHandler = config.onConfirm || (() => {});
  const finalConfig = {
    title: '',
    message: 'Are you sure?',
    type: 'warning',
    closeOnEscape: true,
    closeOnClickOutside: false,
    overlayClassName: '',
    zIndex: 999999,
    ...config,
    buttons: config.buttons || [
      { label: 'Yes', onClick: yesHandler },
      { label: 'No',  onClick: () => {} },
    ],
  };

  cleanupConfirmation();
  const container = getConfirmationContainer();
  if (finalConfig.zIndex) container.style.zIndex = finalConfig.zIndex;

  confirmationRoot = createRoot(container);
  confirmationRoot.render(
    <ConfirmationDialog
      config={finalConfig}
      onClose={() => {
        // Escape / backdrop dismissal — lets the promise API below settle.
        finalConfig.onDismiss?.();
        cleanupConfirmation();
      }}
    />
  );
};

/**
 * Promise-based confirm — the preferred API for new code:
 *
 *   if (await confirm({ title: 'Delete record?', type: 'danger' })) { ... }
 *
 * Resolves `true` on confirm, `false` on cancel/Escape/backdrop. Renders the
 * same dialog as `confirmAlert`, so the two APIs share one visual.
 */
export const confirm = ({
  title = 'Confirm Action',
  message = 'Are you sure?',
  type = 'warning',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  ...rest
} = {}) =>
  new Promise(resolve => {
    confirmAlert({
      title,
      message,
      type,
      closeOnClickOutside: true,
      ...rest,
      onDismiss: () => resolve(false),
      buttons: [
        { label: cancelText, variant: 'secondary', onClick: () => resolve(false) },
        {
          label: confirmText,
          variant: type === 'danger' ? 'danger' : 'primary',
          onClick: () => resolve(true),
        },
      ],
    });
  });

export default confirmAlert;
